"use client";

import React from "react";
import ReactPhoneInput from "react-phone-number-input";
import type { SizeType } from "antd/es/config-provider/SizeContext";
import type { CountryCode } from "libphonenumber-js";
import "react-phone-number-input/style.css";
import "./PhoneInput.css";

/* ── Inline globe SVG (avoids @ant-design/icons dep) ── */

function GlobeIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
    </svg>
  );
}

/* ── Size context (threads the size to the nested input) ── */

const SizeCtx = React.createContext<SizeType>("middle");

/* ── Size → inline-style map (matches antd v6 outlined Input tokens) ── */

const SIZE_STYLES: Record<
  NonNullable<SizeType>,
  { padding: string; borderRadius: number; height: number }
> = {
  small: { padding: "0px 7px", borderRadius: 4, height: 24 },
  middle: { padding: "4px 11px", borderRadius: 6, height: 32 },
  large: { padding: "7px 11px", borderRadius: 8, height: 40 },
};

/* ── Plain <input> adapter (no antd CSS-in-JS) ── */

type NativeInputAdapterProps = {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  name?: string;
  autoComplete?: string;
  disabled?: boolean;
  placeholder?: string;
};

const NativeInputAdapter = React.forwardRef<
  HTMLInputElement,
  NativeInputAdapterProps
>((props, ref) => {
  const size = React.useContext(SizeCtx);
  const s = SIZE_STYLES[size ?? "middle"];

  return (
    <input
      ref={ref}
      {...props}
      className={`oe-phone-input__input oe-phone-input__input--${size ?? "middle"}`}
      style={{
        boxSizing: "border-box",
        width: "100%",
        margin: 0,
        padding: s.padding,
        fontSize: "inherit",
        lineHeight: 1.5714,
        height: s.height,
        fontFamily: "inherit",
        color: "rgba(0, 0, 0, 0.88)",
        background: "#ffffff",
        border: "1px solid #d9d9d9",
        borderRadius: s.borderRadius,
        outline: "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    />
  );
});

NativeInputAdapter.displayName = "NativeInputAdapter";

/* ── Public interface ── */

export interface PhoneInputProps {
  /** Current value (E.164 or national format). */
  value?: string;
  /** Called with the E.164 value or `undefined` when cleared. */
  onChange?: (value: string | undefined) => void;
  /** Input placeholder text. */
  placeholder?: string;
  /** Disable the input. */
  disabled?: boolean;
  /** Additional CSS class on the wrapper. */
  className?: string;
  /** Default / forced country. Default: `"US"`. */
  country?: CountryCode;
  /**
   * When `true`, the country selector is visible so the user can pick a
   * different country.  The number is always formatted in national style
   * (no +1 prefix).  Default: `false`.
   *
   * Ignored when `showInternationalToggle` is `true` — the component manages
   * the value internally instead.
   */
  isInternational?: boolean;
  /** Show a globe icon button on the right that lets the user reveal the
   *  country selector.  Default: `false`. */
  showInternationalToggle?: boolean;
  /**
   * Visual status — `"error"` adds a red border matching antd convention.
   */
  status?: "error" | "";
  /** Antd Input size. Default: `"middle"`. */
  size?: SizeType;
  /** Optional label rendered above the input. */
  label?: React.ReactNode;
  /**
   * Accent color for focus ring and active globe icon.
   * Pass the same value as your antd ConfigProvider `colorPrimary`.
   * Defaults to `"#000000"`.
   */
  accentColor?: string;
}

export function PhoneInput({
  value,
  onChange,
  placeholder = "(555) 123-4567",
  disabled = false,
  className,
  country = "US",
  isInternational = false,
  showInternationalToggle = false,
  status,
  size = "middle",
  label,
  accentColor,
}: PhoneInputProps) {
  const accent = accentColor ?? "#000000";
  const [localInternational, setLocalInternational] = React.useState(false);

  // When the toggle is shown the component owns the international state,
  // otherwise we respect the `isInternational` prop directly.
  const effectiveInternational = showInternationalToggle
    ? localInternational
    : isInternational;

  const handleToggle = React.useCallback(() => {
    setLocalInternational((prev) => !prev);
  }, []);

  const countrySelectProps = React.useMemo(() => {
    if (effectiveInternational) return undefined;
    return { style: { display: "none" } };
  }, [effectiveInternational]);

  const wrapperCls = [
    "oe-phone-input",
    effectiveInternational ? "" : "oe-phone-input--us-only",
    status === "error" ? "oe-phone-input--error" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={wrapperCls}
      style={
        {
          width: "100%",
          "--oe-phone-accent": accent,
        } as React.CSSProperties
      }
    >
      {label && (
        <div style={{ marginBottom: 8 }}>
          <label className="oe-phone-input__label">{label}</label>
        </div>
      )}
      <div style={{ position: "relative", width: "100%" }}>
        <SizeCtx.Provider value={size}>
          <ReactPhoneInput
            style={{ width: "100%" }}
            placeholder={placeholder}
            defaultCountry={country}
            value={value}
            international={false}
            onChange={(nextValue) => onChange?.(nextValue)}
            disabled={disabled}
            country={effectiveInternational ? undefined : country}
            countrySelectProps={countrySelectProps}
            inputComponent={NativeInputAdapter}
          />
        </SizeCtx.Provider>
        {showInternationalToggle && (
          <button
            type="button"
            className={`oe-phone-input__globe${
              effectiveInternational ? " oe-phone-input__globe--active" : ""
            }`}
            onClick={handleToggle}
            aria-label="Toggle international phone input"
            aria-pressed={effectiveInternational}
          >
            <GlobeIcon />
          </button>
        )}
      </div>
    </div>
  );
}

export default PhoneInput;
